import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './PuzzlePage.css';

const sweet = withReactContent(Swal);
const allowed = ['parents', 'friends'];

function formatTime(seconds) {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export default function VideoUpload() {
  const { category } = useParams();
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [saved, setSaved] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showVideoBox, setShowVideoBox] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (!allowed.includes(category)) {
      setError('Kategori tidak valid.');
    }
  }, [category]);

  useEffect(() => {
    return () => {
      stopStream();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const beginRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;

    chunksRef.current = [];
    const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = url;
        videoRef.current.controls = true;
        videoRef.current.muted = false;
        videoRef.current.style.transform = 'none';
        videoRef.current.play().catch(()=>{});
      }
      stopStream();
    };
    recorder.start();
    setIsRecording(true);
    setElapsed(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);
  };

  const startRecord = async () => {
    try {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setSaved(false);
      setShowVideoBox(true);

      const media = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = media;
      if (videoRef.current) {
        videoRef.current.srcObject = media;
        videoRef.current.muted = true;
        videoRef.current.controls = false;
        videoRef.current.style.transform = 'scaleX(-1)';
        videoRef.current.play().catch(()=>{});
      }
      setCountdown(3);
      if (countdownRef.current) clearInterval(countdownRef.current);
      let counter = 3;
      countdownRef.current = setInterval(() => {
        counter -= 1;
        if (counter <= 0) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setCountdown(0);
          beginRecording();
        } else {
          setCountdown(counter);
        }
      }, 1000);
      setError(null);
    } catch (e) {
      setError('Tidak bisa mengakses kamera/mikrofon: ' + e.message);
      setShowVideoBox(false);
      stopStream();
    }
  };

  const stopRecord = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    chunksRef.current = [];
    setError(null);
    setSaved(false);
    setElapsed(0);
    setShowVideoBox(false);
  };

  const handleSave = async () => {
    if (!chunksRef.current.length) {
      setError('Tidak ada video yang direkam.');
      return;
    }
    const blob = new Blob(chunksRef.current, { type: 'video/webm' });
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        setSaving(true);
        const res = await fetch(`https://api.laviopaperie.com/upload-video/${category}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ video: reader.result })
        });
        if (!res.ok) throw new Error('Gagal menyimpan ke server');
        setSaved(true);
        await sweet.fire({
          title: 'Thank you! 💞',
          // html: 'Video kamu sukses disimpan. Nanti aku putar ya!',
          confirmButtonText: 'Yeay!',
          confirmButtonColor: '#a96df0',
          background: '#fef5ff',
          color: '#775acc',
          allowOutsideClick: false,
          showClass: { popup: 'swal-pop-in' },
          hideClass: { popup: 'swal-pop-out' }
        });
      } catch (e) {
        alert('Gagal menyimpan video: ' + e.message);
      } finally {
        setSaving(false);
      }
    };
    reader.readAsDataURL(blob);
  };

  const disableActions = saved;

  return (
    <div className="surprise-wrapper">
      <div className="surprise-card" style={{maxWidth:'680px'}}>
        <div className="surprise-title">Upload Video🎥</div>
        {error && <div className="cam-error" style={{marginBottom:'12px'}}>{error}</div>}
        {isRecording && <div className="cam-hint">Recording... {formatTime(elapsed)}</div>}
        {!isRecording && previewUrl && !saved && (
          <div className="cam-hint">Durasi: {formatTime(elapsed)}</div>
        )}
        {(showVideoBox || previewUrl) ? (
          <div className="video-box">
            <video
              ref={videoRef}
              className="surprise-video"
              controls={!isRecording && !!previewUrl}
              autoPlay={isRecording}
              muted={!previewUrl}
            ></video>
            {countdown > 0 && <div className="cam-countdown">{countdown}</div>}
          </div>
        ) : (
          <div className="cam-placeholder">Klik Start Record untuk mulai</div>
        )}
        <div className="cam-actions" style={{marginTop:'16px'}}>
          {!disableActions && !isRecording && countdown === 0 && !previewUrl && (
            <button className="puzzle-btn" onClick={startRecord}>Start Record</button>
          )}
          {!disableActions && isRecording && (
            <button className="puzzle-btn" onClick={stopRecord}>Stop</button>
          )}
          {!disableActions && !isRecording && previewUrl && (
            <button className="puzzle-btn ghost" onClick={retake}>Retake</button>
          )}
          {!disableActions && !isRecording && previewUrl && (
            <button className="puzzle-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          )}
          {disableActions && <div className="puzzle-win">video saved!💜</div>}
        </div>
      </div>
    </div>
  );
}
